import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import {
  IDeleteDataResponseAndPayload,
  TResponse,
  TResponseWithQData,
} from "../../../types/common";
import {
  IQuote,
  IQuoteCreatePayload,
  IQuoteSendPayload,
  IQuoteUpdatePayload,
} from "../../../types/api/quote";
import {
  listQuote,
  quoteDetail,
  createQuote,
  updateQuote,
  deleteQuote,
  sendQuote,
  convertQuoteToJob,
  acceptQuoteOnBehalf,
} from "./callbacks";

export const useQuoteList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IQuote[]>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponseWithQData<IQuote[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IQuote[]>, Error>({
    queryKey: [API_ENDPOINTS.QUOTE_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listQuote(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams(params as QData);
    },
  };
};

export const useQuoteDetail = (
  id: string | number,
  options?: Omit<
    UseQueryOptions<TResponse<IQuote>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponse<IQuote>, Error> => {
  return useQuery<TResponse<IQuote>, Error>({
    queryKey: [API_ENDPOINTS.QUOTE_DETAIL, id],
    queryFn: async () => {
      const res = await quoteDetail(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

export const useQuoteStats = (
  options?: Omit<
    UseQueryOptions<TResponse<Record<string, number>>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponse<Record<string, number>>, Error> => {
  return useQuery<TResponse<Record<string, number>>, Error>({
    queryKey: [API_ENDPOINTS.QUOTE_STATS],
    queryFn: async () => {
      const { default: axios } = await import("../../../lib/axios");
      const res = await axios.get(API_ENDPOINTS.QUOTE_STATS);
      return res.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQuoteCreate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IQuote>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IQuote>,
    unknown,
    IQuoteCreatePayload
  > = {},
): UseMutationResult<TResponse<IQuote>, unknown, IQuoteCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IQuote>, unknown, IQuoteCreatePayload>({
    mutationFn: async (data) => {
      const res = await createQuote(data);
      return res.data;
    },
    onSuccess: (res, vars, context) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.QUOTE_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: [res.data, ...old.data],
            extra: { ...old.extra, total: (old.extra?.total ?? 0) + 1 },
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, context);
    },
    onError: (error, vars, context) => {
      onError?.(error);
      options?.onError?.(error, vars, context);
    },
    ...options,
  });
};

export const useQuoteUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IQuote>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IQuote>,
    unknown,
    IQuoteUpdatePayload
  > = {},
): UseMutationResult<TResponse<IQuote>, unknown, IQuoteUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IQuote>, unknown, IQuoteUpdatePayload>({
    mutationFn: async (data) => {
      const res = await updateQuote(data);
      return res.data;
    },
    onSuccess: (res, vars, context) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.QUOTE_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: old.data.map((q: IQuote) =>
              q.id === res.data?.id ? res.data : q,
            ),
          };
        },
      );
      queryClient.setQueryData([API_ENDPOINTS.QUOTE_DETAIL, res.data?.id], res);
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, context);
    },
    onError: (error, vars, context) => {
      onError?.(error);
      options?.onError?.(error, vars, context);
    },
    ...options,
  });
};

export const useQuoteDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  > = {},
): UseMutationResult<
  TResponse<IDeleteDataResponseAndPayload>,
  unknown,
  IDeleteDataResponseAndPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  >({
    mutationFn: async (data) => {
      const res = await deleteQuote(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.QUOTE_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const deletedId = Number(vars.ids);
          return {
            ...old,
            data: old.data.filter((q: IQuote) => q.id !== deletedId),
            extra: {
              ...old.extra,
              total: Math.max((old.extra?.total ?? 1) - 1, 0),
            },
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useQuoteConvertToJob = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<any>) => void;
    onError?: (error: unknown) => void;
  } = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<any>, unknown, { id: number }>({
    mutationFn: async (data) => {
      const res = await convertQuoteToJob(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_STATS] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.JOB_LIST] });
      onSuccess?.(res);
    },
    onError: (error, ...rest) => {
      onError?.(error);
    },
  });
};

export const useQuoteAcceptOnBehalf = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<any>) => void;
    onError?: (error: unknown) => void;
  } = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<any>, unknown, { id: number }>({
    mutationFn: async (data) => {
      const res = await acceptQuoteOnBehalf(data);
      return res.data;
    },
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_STATS] });
      onSuccess?.(res);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useQuoteSend = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (
      res: TResponse<{ token: string; public_url: string; pdf_url?: string }>,
    ) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<{ token: string; public_url: string; pdf_url?: string }>,
    unknown,
    IQuoteSendPayload
  > = {},
): UseMutationResult<
  TResponse<{ token: string; public_url: string; pdf_url?: string }>,
  unknown,
  IQuoteSendPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const res = await sendQuote(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      // Invalidate quote detail so status updates to "sent"
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.QUOTE_DETAIL, vars.id],
      });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.QUOTE_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};
